import { Card } from '@/common/ui/card'
import {
	GetOverallAnalyticsResponse,
	GetOnlineActiveUserResponse,
} from '@/modules/dashboard/type'
import { Skeleton } from '@/common/ui/skeleton'
import {
	ArrowDown,
	ArrowUp,
	Wallet,
	Gift,
	RefreshCw,
	DollarSign,
	Coins,
	CircleDot,
	Users,
	UserPlus,
	ArrowRight,
	Sparkles,
	RotateCcw,
	Calendar,
	Activity,
	Info,
} from 'lucide-react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'

type TrendType = 'up' | 'down'

interface DashboardCardProps {
	data?: GetOverallAnalyticsResponse
	isLoading?: boolean
	onlineUsersData?: GetOnlineActiveUserResponse
	isLoadingOnlineUsers?: boolean
}

interface MetricCardProps {
	label: string
	info?: string
	tooltip?: string
	value: number
	icon: React.ElementType
	trend?: TrendType
	isLoading?: boolean
	bgColor?: string
	iconColor?: string
	linkTo?: string
	viewButtonText?: string
}

type MainMetricConfig = Omit<MetricCardProps, 'isLoading'> & {
	cardLoading?: boolean
}

function MetricCard({
	label,
	value,
	info,
	tooltip,
	icon: Icon,
	trend,
	isLoading = false,
	bgColor = 'bg-blue-50 dark:bg-blue-950',
	iconColor = 'text-blue-600 dark:text-blue-400',
	linkTo,
	viewButtonText = 'ดูรายชื่อผู้ใช้',
}: MetricCardProps) {
	return (
		<Card className="overflow-hidden transition-all hover:shadow-lg">
			<div className="p-6">
				{isLoading ? (
					<>
						<Skeleton className="mb-4 h-4 w-24" />
						<Skeleton className="h-8 w-32" />
					</>
				) : (
					<>
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-4">
								<div className="flex items-center gap-2">
									<div className={`rounded-lg p-2 ${bgColor}`}>
										<Icon className={`h-5 w-5 ${iconColor}`} />
									</div>
									<h3 className="text-sm font-medium text-muted-foreground">
										{label}
									</h3>
									{tooltip && (
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger asChild>
													<Info className="h-3.5 w-3.5 cursor-help text-muted-foreground/60 hover:text-muted-foreground" />
												</TooltipTrigger>
												<TooltipContent
													side="top"
													className="max-w-56 text-center"
												>
													{tooltip}
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									)}
								</div>
								{linkTo && (
									<Link
										to={linkTo}
										className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 hover:underline"
									>
										{viewButtonText}
										<ArrowRight className="h-3.5 w-3.5" />
									</Link>
								)}
							</div>
							{trend && (
								<div
									className={`flex items-center ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
								>
									{trend === 'up' ? (
										<ArrowUp className="h-4 w-4" />
									) : (
										<ArrowDown className="h-4 w-4" />
									)}
								</div>
							)}
						</div>
						<div className="mt-4">
							<p className="select-text text-2xl font-bold text-foreground">
								{value.toLocaleString()}
							</p>
						</div>
						<div className="mt-2">
							<p className="text-sm text-muted-foreground">
								<div className="flex items-center gap-1">
									<CircleDot className={clsx('h-2 w-2', iconColor)} />
									<span className="select-text">{info}</span>
								</div>
							</p>
						</div>
					</>
				)}
			</div>
		</Card>
	)
}

export default function DashboardCard({
	data,
	isLoading = false,
	onlineUsersData,
	isLoadingOnlineUsers = false,
}: DashboardCardProps) {
	const { dateFilter } = useDateFilter()
	const { startDate, endDate } = dateFilter

	if (isLoading && !data) {
		return (
			<div className="space-y-6">
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
					{Array.from({ length: 8 }).map((_, index) => (
						<MetricCard
							key={index}
							label=""
							value={0}
							icon={Wallet}
							isLoading={true}
						/>
					))}
				</div>
			</div>
		)
	}

	if (!data) {
		return (
			<div className="flex h-[50vh] items-center justify-center">
				<p className="text-lg text-gray-600">
					ไม่พบข้อมูลที่ต้องการ กรุณาลองใหม่อีกครั้ง
				</p>
			</div>
		)
	}

	// Format timestamp if available
	const formattedTimestamp = onlineUsersData?.timestamp
		? format(new Date(onlineUsersData.timestamp), 'dd/MM/yyyy HH:mm:ss', {
				locale: th,
			})
		: ''

	const mainMetrics: MainMetricConfig[] = [
		{
			label: 'ยอดฝากทั้งหมด',
			value: data.totalDepositAmount,
			icon: Wallet,
			trend: 'up',
			bgColor: 'bg-green-50 dark:bg-green-950',
			iconColor: 'text-green-600 dark:text-green-400',
			tooltip:
				'ไม่รวมโบนัสโปรโมชั่นและเครดิตฟรี แสดงเฉพาะยอดฝากจริงของผู้เล่นเท่านั้น',
			info: `ดิฟ - ${data.totalUnverifiedDepositAmount.toLocaleString()} บาท`,
			linkTo: `/operator/get-player-list/player-with-deposit${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่มียอดฝาก',
		},
		{
			label: 'ยอดถอนทั้งหมด',
			value: data.totalWithdrawAmount,
			icon: DollarSign,
			trend: 'down',
			bgColor: 'bg-red-50 dark:bg-red-950',
			iconColor: 'text-red-600 dark:text-red-400',
			info: `ค้างถอน ${data.totalPendingWithdrawAmount.toLocaleString()} บาท`,
			linkTo: `/operator/get-player-list/player-with-withdraw${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่มียอดถอน',
		},
		{
			label: 'กำไร',
			value: data.totalDepositAmount - data.totalWithdrawAmount,
			icon: Coins,
			bgColor: 'bg-blue-50 dark:bg-blue-950',
			iconColor: 'text-blue-600 dark:text-blue-400',
			tooltip: 'ยอดฝากจริง − ยอดถอนทั้งหมด',
			info:
				data.totalDepositAmount - data.totalWithdrawAmount > 0
					? 'กำไร'
					: 'ขาดทุน',
		},
		{
			label: 'ยอดฝากเงินแบบไม่ฝากโปร',
			value: data.totalDepositAmountWithoutPromotion,
			icon: Wallet,
			trend: 'up',
			bgColor: 'bg-cyan-50 dark:bg-cyan-950',
			iconColor: 'text-cyan-600 dark:text-cyan-400',
			tooltip:
				'ไม่รวมโบนัสโปรโมชั่นและเครดิตฟรี แสดงเฉพาะยอดฝากจริงที่ไม่มีการแนบโปรโมชั่น',
			info: `${data.totalDepositCountWithoutPromotion} ครั้ง`,
			linkTo: `/operator/get-player-list/player-with-deposits-without-promotion${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่ฝากเงินแบบไม่ฝากโปร',
		},
		{
			label: 'ยอดฝากเงินแบบติดเงื่อนไข',
			value: data.totalDepositAmount - data.totalDepositAmountWithoutPromotion,
			icon: Gift,
			bgColor: 'bg-purple-50 dark:bg-purple-950',
			iconColor: 'text-purple-600 dark:text-purple-400',
			tooltip:
				'ไม่รวมโบนัสโปรโมชั่นและเครดิตฟรี แสดงเฉพาะยอดฝากจริงที่แนบโปรโมชั่น',
			info: `${data.totalPromotionBonusCount} ครั้ง`,
			linkTo: `/operator/get-player-list/player-with-deposit-promotion${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่ฝากด้วยโปรโมชั่น',
		},
		{
			label: 'เครดิตค้างพร้อมถอน',
			value: data.totalMainWalletCredit,
			icon: RefreshCw,
			bgColor: 'bg-indigo-50 dark:bg-indigo-950',
			iconColor: 'text-indigo-600 dark:text-indigo-400',
			info: `จากผู้เล่น ${data.totalMainWalletCount} คน`,
		},
		{
			label: 'เครดิตค้างติดเทิร์น',
			value: data.totalPromotionWalletCredit,
			icon: RefreshCw,
			bgColor: 'bg-indigo-50 dark:bg-indigo-950',
			iconColor: 'text-indigo-600 dark:text-indigo-400',
			info: `จากผู้เล่น ${data.totalPromotionWalletCount} คน`,
		},
		{
			label: 'เครติด AFF',
			value: data.totalInviteFriendRewardTransfer,
			icon: RefreshCw,
			bgColor: 'bg-indigo-50 dark:bg-indigo-950',
			iconColor: 'text-indigo-600 dark:text-indigo-400',
			info: `จากผู้เล่น ${data.totalPlayerReceiveInviteFriendRewardTransfer} คน`,
		},
		{
			label: 'เครดิตจากกิจกรรม',
			value: data.totalEventCreditTransfer,
			icon: Gift,
			bgColor: 'bg-violet-50 dark:bg-violet-950',
			iconColor: 'text-violet-600 dark:text-violet-400',
			info: 'รวมเครดิตโอนจากกระเป๋ากิจกรรมทั้งหมด',
		},
		{
			label: 'ผู้ใช้ที่มียอดเดิมพัน',
			value: data.totalPlayCount,
			icon: Users,
			bgColor: 'bg-orange-50 dark:bg-orange-950',
			iconColor: 'text-orange-600 dark:text-orange-400',
			info: `ฝากเงิน ${data.totalDepositCount} คน`,
			linkTo: `/operator/get-player-list/active-player${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่มีการเดิมพัน',
		},
		{
			label: 'จำนวนสมัครใหม่',
			value: data.totalNewPlayer,
			icon: UserPlus,
			bgColor: 'bg-teal-50 dark:bg-teal-950',
			iconColor: 'text-teal-600 dark:text-teal-400',
			info: `เติมครั้งแรก ${data.totalFirstDepositTime} คน`,
			linkTo: `/operator/registered-players${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่สมัครใหม่',
		},
		{
			label: 'ผู้เล่นใหม่ที่มียอดฝาก',
			value: data.totalNewPlayerWithDeposit,
			icon: UserPlus,
			bgColor: 'bg-teal-50 dark:bg-teal-950',
			iconColor: 'text-teal-600 dark:text-teal-400',
			info: `ยอดฝาก ${data.totalNewPlayerDepositAmount.toLocaleString()} บาท`,
			tooltip:
				'ผู้เล่นใหม่ หมายถึง ผู้เล่นที่ทำการสมัครในช่วงเวลาของข้อมูลสถิติที่เลือก (เลือกช่วงเวลาได้ที่มุมขวาบนของหน้านี้)',
			linkTo: `/operator/registered-players?hasDeposit=true${startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้เล่น',
		},
		{
			label: 'ยอดฝากผู้เล่นใหม่',
			value: data.totalNewPlayerDepositAmount,
			icon: Wallet,
			trend: 'up',
			bgColor: 'bg-teal-50 dark:bg-teal-950',
			iconColor: 'text-teal-600 dark:text-teal-400',
			info: `จากผู้เล่น ${data.totalNewPlayerWithDeposit.toLocaleString()} คน`,
			tooltip:
				'ผู้เล่นใหม่ หมายถึง ผู้เล่นที่ทำการสมัครในช่วงเวลาของข้อมูลสถิติที่เลือก (เลือกช่วงเวลาได้ที่มุมขวาบนของหน้านี้)',
		},
		{
			label: 'ยอดถอนรอดำเนินการ',
			value: data.totalWithdrawPendingAmount,
			icon: DollarSign,
			bgColor: 'bg-red-50 dark:bg-red-950',
			iconColor: 'text-red-600 dark:text-red-400',
			info: `จากผู้เล่น ${data.totalWithdrawPendingTime} คน`,
		},
		{
			label: 'ยอดถอนรายการไม่สำเร็จ',
			value: data.totalWithdrawFailedAmount,
			icon: DollarSign,
			bgColor: 'bg-red-50 dark:bg-red-950',
			iconColor: 'text-red-600 dark:text-red-400',
			info: `จากผู้เล่น ${data.totalWithdrawFailedTime} คน`,
		},
		{
			label: 'ผู้ใช้ออนไลน์',
			value: onlineUsersData?.totalOnlineUsers || 0,
			icon: Activity,
			bgColor: 'bg-emerald-50',
			iconColor: 'text-emerald-600',
			cardLoading: isLoadingOnlineUsers,
			info: formattedTimestamp
				? `อัปเดตล่าสุด: ${formattedTimestamp}`
				: 'กำลังโหลด...',
		},
		{
			label: 'ค่าธรรมเนียมธุรกรรมฝากเงิน',
			value: data.totalDepositFeeAmount,
			icon: DollarSign,
			bgColor: 'bg-green-50 dark:bg-green-950',
			iconColor: 'text-green-600 dark:text-green-400',
			info: `จำนวนฝาก ${data.totalDepositCount} ครั้ง`,
		},
		{
			label: 'ค่าธรรมเนียมธุรกรรมถอนเงิน',
			value: data.totalWithdrawFeeAmount,
			icon: DollarSign,
			bgColor: 'bg-red-50 dark:bg-red-950',
			iconColor: 'text-red-600 dark:text-red-400',
			info: `จำนวนถอน ${data.totalWithdrawTime} ครั้ง`,
		},
		{
			label: 'ยอดฝากแมนนวล',
			value: data.totalManualDepositAmount,
			icon: Wallet,
			bgColor: 'bg-amber-50 dark:bg-amber-950',
			iconColor: 'text-amber-600 dark:text-amber-400',
			info: `จำนวน ${data.totalManualDepositTime} ครั้ง`,
		},
		{
			label: 'ยอดฝากแมนนวลสำหรับแจกเครดิตฟรี',
			value: data.totalManualFreeCreditAmount,
			icon: Gift,
			bgColor: 'bg-amber-50 dark:bg-amber-950',
			iconColor: 'text-amber-600 dark:text-amber-400',
			info: `จำนวน ${data.totalManualFreeCreditTime} ครั้ง`,
		},
		{
			label: 'แอดมินปรับยอดเครดิต',
			value: data.totalAdminWalletAdjustment,
			icon: RefreshCw,
			bgColor: 'bg-rose-50 dark:bg-rose-950',
			iconColor: 'text-rose-600 dark:text-rose-400',
			info: data.totalAdminWalletAdjustment >= 0 ? 'เพิ่มสุทธิ' : 'ลดสุทธิ',
		},
		{
			label: 'แอดมินเติมเครดิตพาร์ทเนอร์',
			value: data.totalAdminPartnerCredit,
			icon: UserPlus,
			bgColor: 'bg-rose-50 dark:bg-rose-950',
			iconColor: 'text-rose-600 dark:text-rose-400',
			info: 'เติมเข้ากระเป๋าพาร์ทเนอร์',
		},
	]

	const rewardMetrics: MainMetricConfig[] = [
		{
			label: 'รางวัลวงล้อโชค',
			value: data.totalLuckyWheelBonusReward,
			icon: Sparkles,
			bgColor: 'bg-pink-50 dark:bg-pink-950',
			iconColor: 'text-pink-600 dark:text-pink-400',
			info: `จำนวน ${data.totalLuckyWheelQuotaReward.toLocaleString()} โควต้า / ผู้รับ ${data.totalPlayerReceiveLuckyWheel} คน`,
			linkTo: `/operator/get-player-list/player-with-lucky-wheel-receive${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่รับรางวัลวงล้อโชค',
		},
		{
			label: 'รางวัลเช็คอินรายวัน',
			value: data.totalDailyCheckInReward || 0,
			icon: Calendar,
			bgColor: 'bg-blue-50 dark:bg-blue-950',
			iconColor: 'text-blue-600 dark:text-blue-400',
			info: `จำนวนผู้รับ ${data.totalPlayerReceiveDailyCheckIn} คน`,
			linkTo: `/operator/get-player-list/player-with-daily-check-in-receive${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่รับรางวัลเช็คอินรายวัน',
		},
		{
			label: 'รางวัลคืนยอดเสีย',
			value: data.totalReturnLossBonusReward,
			icon: RotateCcw,
			bgColor: 'bg-orange-50 dark:bg-orange-950',
			iconColor: 'text-orange-600 dark:text-orange-400',
			info: `จำนวนผู้รับ ${data.totalPlayerReceiveReturnLoss} คน`,
			linkTo: `/operator/get-player-list/player-with-return-loss-receive${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
			viewButtonText: 'ดูผู้ใช้ที่รับรางวัลคืนยอดเสีย',
		},
		{
			label: 'เครดิตโอนจากวงล้อโชค',
			value: data.totalLuckyWheelRewardTransfer,
			icon: Sparkles,
			bgColor: 'bg-pink-50 dark:bg-pink-950',
			iconColor: 'text-pink-600 dark:text-pink-400',
			info: 'โอนเข้ากระเป๋าหลัก/โปรโมชั่น',
		},
		{
			label: 'เครดิตโอนจากเช็คอินรายวัน',
			value: data.totalDailyCheckInRewardTransfer,
			icon: Calendar,
			bgColor: 'bg-blue-50 dark:bg-blue-950',
			iconColor: 'text-blue-600 dark:text-blue-400',
			info: 'โอนเข้ากระเป๋าหลัก/โปรโมชั่น',
		},
		{
			label: 'เครดิตโอนจากคืนยอดเสีย',
			value: data.totalReturnLossBonusRewardTransfer,
			icon: RotateCcw,
			bgColor: 'bg-orange-50 dark:bg-orange-950',
			iconColor: 'text-orange-600 dark:text-orange-400',
			info: 'โอนเข้ากระเป๋าหลัก/โปรโมชั่น',
		},
		{
			label: 'เครดิตโอนจากคอมมิชชั่น',
			value: data.totalCommissionRewardTransfer,
			icon: Coins,
			bgColor: 'bg-amber-50 dark:bg-amber-950',
			iconColor: 'text-amber-600 dark:text-amber-400',
			info: 'โอนเข้ากระเป๋าหลัก/โปรโมชั่น',
		},
		{
			label: 'เครดิตโอนจากพาร์ทเนอร์',
			value: data.totalPartnerRewardTransfer,
			icon: Users,
			bgColor: 'bg-teal-50 dark:bg-teal-950',
			iconColor: 'text-teal-600 dark:text-teal-400',
			info: 'โอนเข้ากระเป๋าหลัก/โปรโมชั่น',
		},
	]

	return (
		<div className="space-y-8">
			<div className="space-y-6">
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
					{mainMetrics.map((metric, index) => (
						<MetricCard
							key={index}
							label={metric.label}
							value={metric.value}
							icon={metric.icon}
							trend={metric.trend}
							bgColor={metric.bgColor}
							iconColor={metric.iconColor}
							info={metric.info}
							tooltip={metric.tooltip}
							isLoading={metric.cardLoading ?? false}
							linkTo={metric.linkTo}
							viewButtonText={metric.viewButtonText}
						/>
					))}
				</div>
			</div>

			<div className="space-y-6">
				<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
					รางวัลและโบนัสจากกิจกรรม
				</h3>
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
					{rewardMetrics.map((metric, index) => (
						<MetricCard
							key={index}
							label={metric.label}
							value={metric.value}
							icon={metric.icon}
							trend={metric.trend}
							bgColor={metric.bgColor}
							iconColor={metric.iconColor}
							info={metric.info}
							tooltip={metric.tooltip}
							isLoading={metric.cardLoading ?? false}
							linkTo={metric.linkTo}
							viewButtonText={metric.viewButtonText}
						/>
					))}
				</div>
			</div>
		</div>
	)
}
